import { TOption, TOptionType } from "../../Components/Select";
import { IControl, IControlCommandBase } from "../controls/types";
import { IDevice } from "../devices/types";
import { t } from "i18next";

export type TDeviceFilter = (device: IDevice) => boolean;
export type TDeviceOption = TOption<number>;
export const getDeviceOptions = (devices: IDevice[]): TDeviceOption[] => {
  return devices
    .map(function (device): TDeviceOption {
      return { label: device.name, value: device.id };
    })
    .sort(function (a: TDeviceOption, b: TDeviceOption) {
      if (a.label && b.label) {
        return a.label.localeCompare(b.label);
      }
      return a.value - b.value;
    });
};

const makeOptions = (values: TOptionType[]) => {
  return values.reduce((options: TOption<TOptionType>[], value) => {
    options.push({ label: t((value as string).toString()), value: value });
    return options;
  }, []);
};

export const getTopicOptions = (
  devices: IDevice[],
  device_id: number | undefined
): TOption<TOptionType>[] => {
  const device = devices.find((d) => d.id === device_id);
  const topics = device
    ? Object.keys(device.values).concat(Object.keys(device.states))
    : [];
  return makeOptions(topics);
};

export const getValueAttributeOptions = (
  devices: IDevice[],
  device_id: number
): TOption<TOptionType>[] => {
  const device = devices.find((d) => d.id === device_id);
  const values = device ? Object.keys(device.values) : [];
  return makeOptions(values);
};

export const getTimestampAttributeOptions = (
  devices: IDevice[],
  device_id: number
): TOption<TOptionType>[] => {
  const device = devices.find((d) => d.id === device_id);
  const timestamps = device ? Object.keys(device.timestamps) : [];
  return makeOptions(timestamps);
};

export const getStateAttributeOptions = (
  devices: IDevice[],
  device_id: number
): TOption<TOptionType>[] => {
  const device = devices.find((d) => d.id === device_id);
  const states = device ? Object.keys(device.states) : [];
  return makeOptions(states);
};

const getDevicesWith = (
  devices: IDevice[],
  property: keyof IDevice
): IDevice[] => {
  return devices.reduce((filtered: IDevice[], device) => {
    if (typeof device[property] === "object") {
      if (Object.keys(device[property] as object).length > 0) {
        filtered.push(device);
      }
    }
    return filtered;
  }, []);
};

export const getDevicesWithValues = (devices: IDevice[]): IDevice[] => {
  return getDevicesWith(devices, "values");
};

export const getDevicesWithTimestamps = (devices: IDevice[]): IDevice[] => {
  return getDevicesWith(devices, "timestamps");
};

export const getDevicesWithStates = (devices: IDevice[]): IDevice[] => {
  return getDevicesWith(devices, "states");
};

export const getDevicesWithValuesOrStates = (devices: IDevice[]): IDevice[] => {
  let devicesWithValues = getDevicesWithValues(devices);
  const devicesWithStates = getDevicesWithStates(devices);
  return devicesWithValues.concat(
    devicesWithStates.filter((item) => devicesWithValues.indexOf(item) < 0)
  );
};

export const getStateAttributeValues = (
  devices: IDevice[],
  device_id: number,
  attributeName: string
) => {
  const device = devices.find((d) => d.id === device_id) as IDevice;
  return device.states[attributeName] || [];
};

export const getStateAttributeValueOptions = (
  devices: IDevice[],
  device_id: number,
  attributeName: string
) => {
  const stateValues = getStateAttributeValues(
    devices,
    device_id,
    attributeName
  );
  return makeOptions(stateValues);
};

export const getControlsWithCommands = (
  controls: IControl[]
): IControlCommandBase[] => {
  return controls.reduce((filtered: IControlCommandBase[], control) => {
    if ("commands" in control) {
      filtered.push(control);
    }
    return filtered;
  }, []);
};

export type TControlOption = TOption<number>;
export const getControlOptions = (controls: IControl[]): TControlOption[] => {
  return controls
    .map(function (control): TControlOption {
      return { label: control.name, value: control.id };
    })
    .sort(function (a: TControlOption, b: TControlOption) {
      if (a.label && b.label) {
        return a.label.localeCompare(b.label);
      }
      return a.value - b.value;
    });
};

export const findControl = (
  controls: IControlCommandBase[],
  control_id: number | undefined
): IControlCommandBase | undefined => {
  return controls.find((d) => d.id === control_id);
};

export const getCommandOptions = (
  control: IControlCommandBase | undefined
): TOption<TOptionType>[] => {
  return control ? makeOptions(Object.keys(control["commands"])) : [];
};

export const getCommandParameterOptions = (
  control: IControlCommandBase | undefined,
  command: string
) => {
  return control ? makeOptions(control["commands"][command]) : [];
};
